import {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import fs from 'react-native-fs';
import ImageViewer from 'react-native-image-zoom-viewer';

import actions, { back } from '../../actions';
import { AppHeader } from '../../combo';
import {
    Button,
    ButtonsGroup,
    Dimensions,
    Icon,
    LoadingIndicator,
    Modal,
    Page,
    Platform,
    React,
    Title,
    View
} from '../../common';
import { Log } from '../../common/Log';
import { getEditedVisit } from '../../selectors';
import { getAttachmentsForVisit } from '../../selectors/forms';
import {
    fileExists,
    resolveImagePath
} from '../../services/device';
import { extractFileNameFromFullPath } from '../../utils/files';
import {
    IOS_DOCS_PATH,
    IOS_TEMP_PATH
} from '../Account/DocumentsPhoto';
import { addToDeletedAttaches } from '../Services/Backup/common/backupData';


export const checkUrls = async (urls, changePath = false) => {
    const res = [];
    for (const item of urls) {
        const url = item.url ?? item?.path;
        const exists = await fileExists(url);
        if (exists) {
            res.push({
                ...item,
                url,
            });
        } else {
            if (url?.startsWith(IOS_TEMP_PATH)) {
                const alternativePath = url.replace(IOS_TEMP_PATH, IOS_DOCS_PATH);
                const alternativeExists = await fileExists(alternativePath);
                if (alternativeExists) {
                    res.push({
                        ...item,
                        path: changePath ? alternativePath : item.path,
                        url: alternativePath,
                    });
                    continue;
                }
            }
            if (url?.startsWith(IOS_DOCS_PATH)) {
                const alternativePath = url.replace(IOS_DOCS_PATH, IOS_TEMP_PATH);
                const alternativeExists = await fileExists(alternativePath);
                if (alternativeExists) {
                    res.push({
                        ...item,
                        path: changePath ? alternativePath : item.path,
                        url: alternativePath,
                    });
                    continue;
                }
            }

            let fileUrl = url?.split?.('/')
                ?.pop();
            if (Platform.OS === 'ios') {
                const tempPath = `${IOS_TEMP_PATH}/${fileUrl}`;
                const docPath = `${IOS_DOCS_PATH}/${fileUrl}`;
                const fullDocPath = `${fs.DocumentDirectoryPath}/${fileUrl}`;
                const fullTempPath = `${fs.CachesDirectoryPath}/${fileUrl}`;
                const docExists = await fileExists(fullDocPath);
                const tempExists = await fileExists(fullTempPath);
                if (docExists) {
                    res.push({
                        ...item,
                        path: docPath,
                        url: docPath,
                    });
                    continue;

                }
                if (tempExists) {
                    res.push({
                        ...item,
                        path: tempPath,
                        url: tempPath,
                    });
                    continue;
                }
            }
            res.push(item);
        }
    }
    return res;
};
export const checkUrlsAndroid = async (urls) => {
    const res = [];
    for (const item of urls) {
        const url = item?.url ?? item?.path;
        const exists = await fileExists(url);
        if (exists) {
            if (url?.startsWith('file://')) {

                res.push({
                    ...item,
                    url,
                });
            } else {
                res.push({
                    ...item,
                    url: `file://${url}`,
                });
            }
        } else {
            const fileName = url?.split?.('/')
                ?.pop
                ?.();
            const cachePath = `${fs.CachesDirectoryPath}/${fileName}`;
            let e = await fileExists(cachePath);
            if (e) {
                res.push({
                    ...item,
                    path: `file://${cachePath}`,
                    url: `file://${cachePath}`,
                });
                continue;
            }
            const dataPath = `${fs.DocumentDirectoryPath}/${fileName}`;
            e = await fileExists(dataPath);
            res.push({
                ...item,
                path: `file://${dataPath}`,
                url: `file://${dataPath}`,
            });
        }
    }
    return res;
};


export const compareObjectsDeep = (a, b, log = false) => {
    const inner = (x, y, path) => {
        if (x === y) {
            if (log) {
                console.log('%ccompareObjectsDeep: equal (strict)', 'color: green', {
                    path,
                    value: x,
                });
            }
            return true;
        }
        if (typeof x !== typeof y) {
            if (log) {
                console.log('%ccompareObjectsDeep: type mismatch', 'color: red', {
                    path,
                    typeA: typeof x,
                    typeB: typeof y,
                    valueA: x,
                    valueB: y,
                });
            }
            return false;
        }
        if (x == null || y == null) {
            if (log) {
                console.log('%ccompareObjectsDeep: one is null/undefined', 'color: red', {
                    path,
                    valueA: x,
                    valueB: y,
                });
            }
            return x === y;
        }
        if (x instanceof Date && y instanceof Date) {
            const eq = x.getTime() === y.getTime();
            if (log) {
                console.log(`%ccompareObjectsDeep: date ${eq ? 'match' : 'mismatch'}`, eq ? 'color: green' : 'color: red', {
                    path,
                    valueA: x.getTime(),
                    valueB: y.getTime(),
                    dateA: x.toISOString(),
                    dateB: y.toISOString(),
                });
            }
            return eq;
        }
        if (x instanceof RegExp && y instanceof RegExp) {
            const eq = x.toString() === y.toString();
            if (log) {
                console.log(`%ccompareObjectsDeep: regexp ${eq ? 'match' : 'mismatch'}`, eq ? 'color: green' : 'color: red', {
                    path,
                    valueA: x.toString(),
                    valueB: y.toString(),
                });
            }
            return eq;
        }
        if (Array.isArray(x)) {
            if (!Array.isArray(y) || x.length !== y.length) {
                if (log) {
                    console.log('%ccompareObjectsDeep: array length/type mismatch', 'color: red', {
                        path,
                        lengthA: x.length,
                        lengthB: Array.isArray(y) ? y.length : 'not array',
                        valueA: x,
                        valueB: y,
                    });
                }
                return false;
            }
            let allMatch = true;
            for (let i = 0; i < x.length; i++) {
                const childPath = `${path}[${i}]`;
                if (!inner(x[i], y[i], childPath)) {
                    allMatch = false;
                    if (log) {
                        console.log('%ccompareObjectsDeep: array element mismatch', 'color: red', {
                            path: childPath,
                            index: i,
                            valueA: x[i],
                            valueB: y[i],
                        });
                    }
                }
            }
            if (log && allMatch) {
                console.log('%ccompareObjectsDeep: array match', 'color: green', {
                    path,
                    length: x.length,
                    valueA: x,
                    valueB: y,
                });
            }
            return allMatch;
        }
        if (typeof x === 'object') {
            const collectKeys = (obj) => {
                const set = new Set();
                for (const k in obj) { // включает унаследованные enumerable свойства
                    set.add(k);
                }
                return Array.from(set)
                    .sort();
            };
            const keysA = collectKeys(x);
            const keysB = collectKeys(y);

            // Создаем снимки объектов в момент сравнения
            const snapshotA = JSON.parse(JSON.stringify(x));
            const snapshotB = JSON.parse(JSON.stringify(y));

            if (keysA.length !== keysB.length || keysA.some(k => !keysB.includes(k))) {
                if (log) {
                    console.log('%ccompareObjectsDeep: object keys length/set mismatch (including inherited)', 'color: red', {
                        path,
                        keysA,
                        keysB,
                        keysOnlyInA: keysA.filter(k => !keysB.includes(k)),
                        keysOnlyInB: keysB.filter(k => !keysA.includes(k)),
                        snapshotA, // снимок на момент сравнения
                        snapshotB, // снимок на момент сравнения
                        originalA: x, // оригинальная ссылка (может измениться)
                        originalB: y, // оригинальная ссылка (может измениться)
                    });
                }
                return false;
            }
            let allPropsMatch = true;
            for (const k of keysA) {
                if (!(k in y)) { // проверяем наличие (own или унаследованного)
                    allPropsMatch = false;
                    if (log) {
                        console.log('%ccompareObjectsDeep: key missing in second object (including inherited)', 'color: red', {
                            path,
                            key: k,
                            valueInA: x[k],
                            snapshotA,
                            snapshotB,
                        });
                    }
                } else {
                    const childPath = `${path}.${k}`;
                    if (!inner(x[k], y[k], childPath)) {
                        allPropsMatch = false;
                        if (log) {
                            console.log('%ccompareObjectsDeep: value mismatch for key', 'color: red', {
                                path: childPath,
                                key: k,
                                valueA: x[k],
                                valueB: y[k],
                            });
                        }
                    }
                }
            }
            if (log && allPropsMatch) {
                console.log('%ccompareObjectsDeep: object match', 'color: green', {
                    path,
                    keys: keysA,
                    snapshotA,
                    snapshotB,
                });
            }
            return allPropsMatch;
        }
        const eq = x === y;
        if (log) {
            console.log(`%ccompareObjectsDeep: primitive/function ${eq ? 'match' : 'mismatch'}`, eq ? 'color: green' : 'color: red', {
                path,
                valueA: x,
                valueB: y,
            });
        }
        return eq;
    };
    return inner(a, b, 'root');
};

const ImageCounter = ({
    data,
    id: initialId,
}) => {
    const position = data.findIndex(({ id }) => id === initialId);
    if (position === -1) {
        console.warn('ImageCounter: Initial ID not found in data', initialId, data);
        return null;
    }


    const total = data.length;
    const bottom = Platform.OS === 'ios' ? 35 : 16;
    const title = Object.R('tiles.positionFromTotal', {
        position: position + 1,
        total,
    });
    return (position === undefined || total === 0 || !data?.length) ? null : (
        <View
            style={{
                position: 'absolute',
                bottom,
                left: 16,
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'transparent',
                zIndex: 10,
            }}
        >
            <Title
                style={{
                    color: 'white',
                    fontSize: 16,
                    backgroundColor: 'transparent',
                }}
            >
                {title}
            </Title>
        </View>
    );
};

const ImageControls = ({
    enabled,
    readOnly,
    onDelete,
    bottom = Platform.OS === 'ios' ? 35 : 16,
}) => {
    return enabled ? (<View
        style={{
            position: 'absolute',
            bottom,
            right: 16,
        }}
    >
        {readOnly ? null : (
            <Icon.Trash.White
                style={{
                    backgroundColor: 'transparent',
                    width: 24,
                    height: 24,
                    borderRadius: 0,
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
                color="#FFFFFF"
                onPress={() => {
                    Page.showModal(
                        <Modal hideClose>
                            <Title
                                style={{
                                    alignSelf: 'center',
                                    textAlign: 'center',
                                }}
                                bold
                                id="titles.docDeleteConfirm"
                            />
                            <ButtonsGroup>
                                <Button
                                    transparent
                                    title="buttons.no"
                                    action={Page.closeModal}
                                />
                                <Button
                                    primary
                                    title="buttons.yes"
                                    action={onDelete}
                                />
                            </ButtonsGroup>
                        </Modal>,
                    );
                }}
            />
        )}
    </View>
    ) : null;
};

export const VisitImagesSlider = Page.register(
    ({
        visitInfo: { attachments: dataVisit = [] } = {},
        visitInfo,
        initialId,
        readOnly,
        manualVisit,
        manualData,
        enableDelete = false,
        setState,
        // isUpdating: upd,
        bottom = Platform.OS === 'ios' ? 35 : 16,
    }) => {

        const [ dataUrls, setDataUrls ] = useState([]);
        const [ currentItemId, setCurrentItemId ] = useState(null);
        const [ isUpdating, setIsUpdating ] = useState(true);

        const prepareDataUrls = useCallback((data) => {
            const urls = data.map(({
                uri,
                path,
                cachePath,
                id,
            }) => ({
                url: resolveImagePath(uri, cachePath || path),
                id,
                path,
                uri,
                cachePath,
            }));

            const normalizeForComparison = (arr) => arr.map(({
                id,
                url,
                uri,
                path,
                cachePath,
            }) => ({
                id,
                url,
                uri,
                path,
                cachePath,
            }));
            const normalizedDataUrls = normalizeForComparison(dataUrls);

            // if (Platform.OS === 'ios') {
            const fn = Platform.OS === 'ios' ? checkUrls : checkUrlsAndroid;
            fn(urls)
                .then((checkedUrls) => {
                    const urls = normalizeForComparison(checkedUrls);
                    if (!compareObjectsDeep(urls, normalizedDataUrls, false)) {
                        setDataUrls(checkedUrls);
                        setCurrentItemId(checkedUrls?.find(({ id }) => id === initialId)?.id ?? checkedUrls[0]?.id ?? null);
                    }
                })
                .catch(() => checkUrls(urls))
                .finally(() => setTimeout(() => setIsUpdating(false), 0));

        }, [ dataUrls, initialId ]);

        useEffect(() => {

            const data = (manualVisit ? manualData : dataVisit) ?? [];
            prepareDataUrls(data, true);


        }, [ manualData,
            dataVisit,
            manualVisit,
            initialId,
            currentItemId,
            visitInfo,
            prepareDataUrls ]);

        const deleteAttachment = useCallback(async () => {
            Page.closeModal();
            const newData = dataUrls.filter(
                ({ id }) => id !== currentItemId,
            );
            const img = extractFileNameFromFullPath(dataUrls?.find?.(d => d?.id === currentItemId)?.path);
            if (img) {
                await addToDeletedAttaches(img);
            }
            if (manualVisit) {
                await actions.setFormValue(
                    'attachmentsForVisit',
                    newData,
                );
            } else {
                await actions.updateEditedVisit({
                    attachments: newData,
                });
            }
            if (!newData.length) {
                back();
            } else {
                const currentItemIndex = dataUrls.findIndex(
                    ({ id }) => id === currentItemId,
                );
                const newInitialIndex =
                    currentItemIndex === dataUrls.length - 1
                        ? currentItemIndex - 1
                        : currentItemIndex;
                const itemId = newData?.[newInitialIndex]?.id;
                setCurrentItemId(itemId);
                setIsUpdating(true);
                setState({ initialId: itemId });
                setTimeout(() => prepareDataUrls(newData, true), 0);
            }
        }, [ dataUrls,
            manualVisit,
            currentItemId,
            prepareDataUrls ]);

        const currentIndex = useMemo(() => {
            if (!currentItemId) {
                return 0;
            }
            const idx = dataUrls.findIndex(({ id }) => id === initialId);
            if (idx === -1) {
                console.warn('Current item ID not found in dataUrls', currentItemId, dataUrls);
                return 0;
            }
            return idx;
        }, [ currentItemId,
            dataUrls,
            initialId ]);

        return (
            <Page
                backgroundColor="black"
                noscroll
            >
                <View
                    style={{
                        flex: 1,
                        backgroundColor: 'black',
                    }}
                >
                    {!isUpdating ? (
                        <>
                            <Log
                                value={{
                                    dataUrls,
                                    currentIndex,
                                }}
                                display={false}
                                text={'ImageViewer'}/>
                            <ImageViewer
                                style={{ marginBottom: 48 }}
                                index={currentIndex}
                                enablePreload
                                saveToLocalByLongPress={false}
                                imageUrls={dataUrls}
                                flipThreshold={Dimensions.get('window').width / 2}
                                renderIndicator={() => null}
                                onChange={(index) => {
                                    const i = dataUrls?.[index]?.id;
                                    setCurrentItemId(i);
                                }}
                            />
                        </>
                    ) : (
                        <View
                            style={{
                                flex: 1,
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <LoadingIndicator indicatorSize={100}/>
                        </View>
                    )}
                    <ImageCounter
                        data={dataUrls}
                        id={currentItemId}/>
                    <ImageControls
                        enabled={enableDelete}
                        readOnly={readOnly}
                        onDelete={deleteAttachment}
                        bottom={bottom}/>
                </View>
            </Page>
        );
    },
    {
        visitInfo: { from: getEditedVisit },
        manualData: { from: getAttachmentsForVisit },
    },
    () => ({ header: () => <AppHeader/> }),
);
